import { AxiosError } from "axios";
import { useMutation } from "@tanstack/react-query";

import {
    IAddNoteMessage,
    IAddNoteMessageResponse,
    IDeleteNoteContentDocument,
    IDeleteNoteMessage,
    IDeleteNoteMessageResponse,
    IFlagNote,
    INoteDocument,
} from "../types/notes.types";
import { IApiResponse } from "../../../utils/types/api-response";
import {
    addNoteContentDocument,
    addNoteMessages,
    deleteNoteContentDocument,
    deleteNoteMessage,
    flagNote,
} from "../services/notes.services";

export function useDeleteMessage(noteId: string) {
    return useMutation<
        IApiResponse<IDeleteNoteMessageResponse>,
        AxiosError<IApiResponse<IDeleteNoteMessageResponse>>,
        IDeleteNoteMessage
    >({
        mutationKey: ["delete-message", noteId],
        mutationFn: (data: IDeleteNoteMessage) =>
            deleteNoteMessage(noteId, data),
    });
}

export function useAddNoteMessaging(noteId: string) {
    return useMutation<
        IApiResponse<IAddNoteMessageResponse>,
        AxiosError<IApiResponse<IAddNoteMessageResponse>>,
        IAddNoteMessage
    >({
        mutationKey: ["add_message", noteId],
        mutationFn: (data: IAddNoteMessage) => addNoteMessages(noteId, data),
    });
}

export function useFlagNote() {
    interface IFlagNoteResponse {
        updated_notes: string[];
        unupdated_notes: string[];
    }

    return useMutation<
        IApiResponse<IFlagNoteResponse>,
        AxiosError<IApiResponse<IFlagNoteResponse>>,
        IFlagNote
    >({
        mutationKey: ["flag-note"],
        mutationFn: (data: IFlagNote) => flagNote(data),
    });
}

export function useAddNoteContentDocument() {
    return useMutation<
        IApiResponse<INoteDocument[]>,
        AxiosError<IApiResponse<INoteDocument[]>>,
        {
            noteId: string;
            formData: FormData;
            onUploadProgress?: (progressEvent: {
                loaded: number;
                total?: number;
            }) => void;
        }
    >({
        mutationKey: ["add-note-content-document"],
        mutationFn: (payload) =>
            addNoteContentDocument(
                payload.noteId,
                payload.formData,
                payload.onUploadProgress
            ),
    });
}

export function useDeleteNoteContentDocument() {
    interface DeleteResponse {
        deleted_documents: INoteDocument[];
    }

    return useMutation<
        IApiResponse<DeleteResponse>,
        AxiosError<IApiResponse<DeleteResponse>>,
        {
            noteId: string;
            dataToSend: IDeleteNoteContentDocument;
        }
    >({
        mutationKey: ["delete-note-content-document"],
        mutationFn: (payload) =>
            deleteNoteContentDocument(payload.noteId, payload.dataToSend),
    });
}
